import {
  ArrowLeftOutlined,
  DownloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Flex,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { getStationeryItemTag } from "../../components/filter/FilterEnum";
import { StationeryItemStatus } from "../../enum/common";
import { IStationeryRequestItem } from "../../models/Hrm";
import {
  useOneStationeryRequest,
  useReviewBulkStationeryRequest,
} from "../../query/HrmQuery";

const HrmStationeryRequestItem = () => {
  const { requestId } = useParams();
  const { Text } = Typography;
  const { data, isLoading, refetch } = useOneStationeryRequest(
    Number(requestId),
  );
  const updateMutation = useReviewBulkStationeryRequest();
  const navigate = useNavigate();

  const [itemRequest, setItemRequest] = useState<IStationeryRequestItem[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  useEffect(() => {
    if (data?.items) {
      setItemRequest(data.items);
    }
  }, [data]);
  const handleItemChange = (
    id: number,
    field: keyof IStationeryRequestItem,
    value: any,
  ) => {
    setItemRequest((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
    if (!selectedRowKeys.includes(id)) {
      setSelectedRowKeys((prev) => [...prev, id]);
    }
  };

  const handleBulkSave = async () => {
    if (selectedRowKeys.length === 0) {
      message.info("Không có thay đổi nào được chọn để lưu.");
      return;
    }

    const itemsToUpdate = itemRequest.filter((item) =>
      selectedRowKeys.includes(item.id),
    );

    try {
      await updateMutation.mutateAsync({
        requestId: Number(requestId),
        items: itemsToUpdate,
      });
      message.success("Cập nhật thành công");
      setSelectedRowKeys([]);
      refetch();
    } catch (e) {
      message.error("Lỗi khi cập nhật");
    }
  };

  return (
    <Flex vertical className="px-3 gap-2 mt-3">
      <Flex justify="space-between" align="center" wrap="wrap" gap="small">
        <Button
          type="default"
          size="large"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className=" bg-white dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          Quay lại
        </Button>
        <Flex gap="small">
          <Popconfirm
            title="Lưu thay đổi?"
            description="Xác nhận lưu các thay đổi vật phẩm?"
            onConfirm={handleBulkSave}
            disabled={selectedRowKeys.length === 0}
          >
            <Button
              type="primary"
              loading={updateMutation.isPending}
              disabled={selectedRowKeys.length === 0}
            >
              Lưu thay đổi ({selectedRowKeys.length})
            </Button>
          </Popconfirm>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => {
              if (!itemRequest?.length) {
                message.warning("Không có dữ liệu để xuất!");
                return;
              }

              const exportData = itemRequest.map((item, index) => ({
                STT: index + 1,
                "Tên vật phẩm": item.product?.name || "",
                "Số lượng": item.quantity,
              }));

              const ws = XLSX.utils.json_to_sheet(exportData);
              ws["!cols"] = [{ wch: 8 }, { wch: 40 }, { wch: 15 }];
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, "Danh_sach_VPP");
              XLSX.writeFile(wb, `Yeu_cau_VPP_${requestId}.xlsx`);
            }}
          >
            Xuất Excel
          </Button>
        </Flex>
      </Flex>
      <div className="p-4 rounded-lg ">
        <div className="md:flex gap-2 mb-4 justify-between">
          <Text className="text-xl">Văn phòng phẩm được đề xuất</Text>
          <Flex className="flex-col md:flex-row gap-3"></Flex>
        </div>

        <Flex className="gap-4 mb-6 text-gray-400 text-sm flex-col lg:flex-row">
          <Text className="border-l white:border-gray-600 pl-4">
            Phòng Ban: {data?.departmentName || "Không xác định"}
          </Text>
          <Text className="border-l white:border-gray-600 pl-4">
            Tổng sản phẩm: {data?.items?.length || 0}
          </Text>
        </Flex>
        <Text>Ghi chú: {data?.note ? data.note : "Không có"}</Text>
      </div>
      <Table
        rowKey="id"
        rowSelection={{
          selectedRowKeys,
          onChange: (newSelectedRowKeys) => {
            const unselectedKeys = selectedRowKeys.filter(
              (key) => !newSelectedRowKeys.includes(key),
            );
            if (unselectedKeys.length > 0) {
              setItemRequest((prev) =>
                prev.map((item) => {
                  if (unselectedKeys.includes(item.id)) {
                    const originalItem = data?.items?.find(
                      (orig) => orig.id === item.id,
                    );
                    return originalItem ? { ...originalItem } : item;
                  }
                  return item;
                }),
              );
            }
            setSelectedRowKeys(newSelectedRowKeys);
          },
        }}
        scroll={{ y: 400, x: 800 }}
        pagination={false}
        loading={isLoading}
        dataSource={itemRequest}
        columns={[
          {
            title: "Mã",
            dataIndex: "id",
            align: "center",
            render: (val) => <Tag color="blue">#{val}</Tag>,
          },

          {
            title: "Tên vật phẩm",
            dataIndex: "product",
            align: "center",
            render: (value, record) => {
              return <Typography.Text strong>{value.name}</Typography.Text>;
            },
            filterDropdown({ setSelectedKeys, selectedKeys, confirm }) {
              return (
                <Input.Search
                  value={selectedKeys[0]}
                  placeholder="Tìm tên vật phẩm"
                  onChange={(e) => {
                    setSelectedKeys(e.target.value ? [e.target.value] : []);
                    confirm({ closeDropdown: false });
                  }}
                />
              );
            },
            filterIcon: (filtered: boolean) => (
              <SearchOutlined
                style={{ color: filtered ? "#1890ff" : undefined }}
              />
            ),
            onFilter: (value, record) =>
              (record.product?.name ?? "Không xác định")
                .toLowerCase()
                .includes((value as string).toLowerCase()),
          },
          {
            title: "Số lượng",
            dataIndex: "quantity",
            align: "center",
            render(value, record) {
              return <Typography.Text strong>{value}</Typography.Text>;
            },
            filterDropdown({ setSelectedKeys, selectedKeys, confirm }) {
              return (
                <InputNumber
                  placeholder="Tìm Số lượng"
                  onChange={(e) => {
                    setSelectedKeys(e ? [e] : []);
                    confirm({ closeDropdown: false });
                  }}
                  onPressEnter={() => confirm()}
                />
              );
            },
            filterIcon: (filtered: boolean) => (
              <SearchOutlined
                style={{ color: filtered ? "#1890ff" : undefined }}
              />
            ),
            onFilter: (value, record) => {
              return record.quantity === value;
            },
          },
          {
            title: "Ghi chú",
            dataIndex: "note",
            align: "center",
            render(value, record) {
              return <Typography.Text strong>{value}</Typography.Text>;
            },
            filterIcon: (filtered: boolean) => (
              <SearchOutlined
                style={{ color: filtered ? "#1890ff" : undefined }}
              />
            ),
            onFilter: (value, record) => {
              return record.note === value;
            },
          },
          {
            title: "Đơn giá",
            align: "center",
            render: (record: any) => {
              return record
                ? (Number(record.product.price) * 1000).toLocaleString(
                    "vi-VN",
                  ) + " VND"
                : "0.000 VND";
            },
          },
          {
            title: "Tổng giá tiền",
            align: "center",
            render: (record: any) => {
              return record
                ? (
                    Number(record.product.price) *
                    1000 *
                    record.quantity
                  ).toLocaleString("vi-VN") + " VND"
                : "0.000 VND";
            },
          },
          {
            title: "Nhận xét",
            dataIndex: "remark",
            align: "center",
            render(value, record) {
              return (
                <Input.TextArea
                  value={value}
                  onChange={(e) =>
                    handleItemChange(record.id, "remark", e.target.value)
                  }
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  placeholder="Ghi chú..."
                />
              );
            },
            filterIcon: (filtered: boolean) => (
              <SearchOutlined
                style={{ color: filtered ? "#1890ff" : undefined }}
              />
            ),
            onFilter: (value, record) => {
              return record.remark === value;
            },
          },
          {
            title:
              selectedRowKeys.length > 0 ? (
                <Flex vertical gap={4} align="center">
                  <span>Trạng thái</span>
                  <Select
                    size="small"
                    placeholder="Cập nhật tất cả"
                    style={{
                      width: "100%",
                      minWidth: 140,
                      fontWeight: "normal",
                    }}
                    value={undefined}
                    onChange={(val) => {
                      setItemRequest((prev) =>
                        prev.map((item) =>
                          selectedRowKeys.includes(item.id)
                            ? {
                                ...item,
                                statusRequest:
                                  val as unknown as StationeryItemStatus,
                              }
                            : item,
                        ),
                      );
                    }}
                    options={[
                      StationeryItemStatus.IS_BUYED,
                      StationeryItemStatus.IS_BUYING,
                      StationeryItemStatus.IS_NOT_BUYED,
                      StationeryItemStatus.IS_NOT_BUY_YET,
                      StationeryItemStatus.IS_RETURNED,
                    ].map((statusValue) => ({
                      value: statusValue,
                      label: getStationeryItemTag(statusValue).label,
                    }))}
                  />
                </Flex>
              ) : (
                "Trạng thái"
              ),
            dataIndex: "statusRequest",
            align: "center",
            render(value, record) {
              return (
                <Select
                  value={value}
                  onChange={(newValue) =>
                    handleItemChange(record.id, "statusRequest", newValue)
                  }
                  style={{ width: "100%", minWidth: 120 }}
                  options={[
                    StationeryItemStatus.IS_BUYED,
                    StationeryItemStatus.IS_BUYING,
                    StationeryItemStatus.IS_NOT_BUYED,
                    StationeryItemStatus.IS_NOT_BUY_YET,
                    StationeryItemStatus.IS_RETURNED,
                  ].map((statusValue) => ({
                    value: statusValue,
                    label: (
                      <Tag
                        color={getStationeryItemTag(statusValue).color}
                        className="m-0 text-sm block text-center"
                      >
                        {getStationeryItemTag(statusValue).label}
                      </Tag>
                    ),
                  }))}
                />
              );
            },
            onFilter: (value, record) => {
              return record.statusRequest === value;
            },
          },
        ]}
      />
    </Flex>
  );
};
export default HrmStationeryRequestItem;
